import { env } from '@/core/env/env';

import { getClientId, getHostedUiOrigin, getUserManager, isOidcInitialized } from './oidc';
import { markCognitoSignOut, type SignOutContinuation } from './redirect-breaker';
import { setBearerToken, type SessionUser } from './session-store';

/**
 * Reads the OAuth callback params, scoped to the registered callback path: matching any URL would
 * let a stray `?error=`/`?code=&state=` push the app into its error state or force a doomed
 * exchange that wipes a valid session's real query params.
 */
export function readCallbackParams(): { code: boolean; error: string | null } {
  const callbackPath = new URL(env.VITE_COGNITO_REDIRECT_SIGN_IN).pathname;
  if (window.location.pathname !== callbackPath) return { code: false, error: null };

  const params = new URLSearchParams(window.location.search);
  return {
    // Both are required. A `code` without `state` is not a callback this client started.
    code: params.has('code') && params.has('state'),
    error: params.get('error'),
  };
}

/**
 * Not cosmetic: leaving `?code=` means a reload replays a spent authorization code, which Cognito
 * rejects. Must run before the router is constructed — a data router captures `window.location` at
 * construction time, so cleaning up afterwards leaves the stale query in `router.state.location`
 * even though the address bar looks right.
 */
export function stripOAuthParams(): void {
  window.history.replaceState({}, '', window.location.pathname);
}

/**
 * Defense-in-depth: catches `?code=`/`?error=` reaching a non-callback route (a stale link, a
 * bookmarked mid-flow URL) that `readCallbackParams` — which matches only the callback path — would
 * otherwise leave for boot to fall through to the stored-token branch. This client is public with
 * no secret, so a code left in the URL/history is exchangeable for real tokens.
 */
export function stripStrayOAuthParams(): void {
  const callbackPath = new URL(env.VITE_COGNITO_REDIRECT_SIGN_IN).pathname;
  if (window.location.pathname === callbackPath) return;

  const params = new URLSearchParams(window.location.search);
  if (!params.has('code') && !params.has('error')) return;

  stripOAuthParams();
}

/**
 * Single-flight: `packages/api`'s 401 handler depends on this being shared, or three
 * simultaneous 401s become three token requests.
 */
let inFlight: Promise<string> | null = null;

export function refreshAndCacheToken(): Promise<string> {
  // Bound to a local first: the finally block nulls the field, so returning it directly
  // would be typed `Promise<string> | null`.
  const pending = (inFlight ??= (async () => {
    try {
      const user = await getUserManager().signinSilent();
      const token = user?.access_token ?? null;
      setBearerToken(token);
      if (!token) throw new Error('Session expired');
      return token;
    } finally {
      // Cleared on failure too, or one failed refresh would poison every later attempt with
      // a permanently rejected promise.
      inFlight = null;
    }
  })());
  return pending;
}

/**
 * Drops the local session once it is proven unusable. Not optional cleanup: oidc-client-ts only
 * stores a user after a *successful* token request, so a refresh token Cognito has rejected
 * (`invalid_grant` — revoked, rotated, or past its lifetime) survives the failure in localStorage.
 * Left there, every later boot replays the same doomed token request and blocks first paint on it
 * for up to the request timeout, forever. A `removeUser` failure is swallowed: the caller is
 * already on a failure path and the in-memory bearer is cleared either way.
 */
export async function discardStoredSession(): Promise<void> {
  setBearerToken(null);
  if (!isOidcInitialized()) return;
  try {
    await getUserManager().removeUser();
  } catch {
    // Storage unavailable or already cleared. Nothing further to do — the bearer is gone and the
    // caller is on a failure path either way.
  }
}

/**
 * Caches the bearer token (access_token) from a stored session; false when there is nothing
 * usable. False also means nothing usable is left in storage: a session that cannot be renewed
 * is discarded here rather than retried on every later boot.
 */
export async function cacheStoredToken(): Promise<boolean> {
  const user = await getUserManager().getUser();
  // `user.expired` derives from the access-token `expires_in`, which is exactly the token cached
  // here, so the freshness check and the cached credential cannot disagree.
  if (user && !user.expired && user.access_token) {
    setBearerToken(user.access_token);
    return true;
  }
  if (user?.refresh_token) {
    try {
      await refreshAndCacheToken();
      return true;
    } catch {
      await discardStoredSession();
      return false;
    }
  }
  await discardStoredSession();
  return false;
}

export async function loadUserProfile(
  fetchProfile: () => Promise<SessionUser>,
): Promise<SessionUser> {
  return fetchProfile();
}

/**
 * Ends both the app's session and Cognito's pool-wide hosted cookie; clearing only the local one
 * lets a "logged out" user be silently signed back in — account takeover on a shared machine. Built
 * by hand with `client_id`+`logout_uri`, the only /logout form that terminates rather than
 * re-entering authorize; the value must be an Allowed sign-out URL, not a callback URL. Discovery
 * advertising `end_session_endpoint` is not RP-Initiated compliance, so `signoutRedirect()` would
 * still build the wrong request. Full form table: docs/adr/authentication.md, Logout.
 */
export async function signOutAndRevoke(
  onTeardownError?: (error: unknown, stage: 'revoke' | 'remove') => void,
  { next = 'login' }: { next?: SignOutContinuation } = {},
): Promise<void> {
  // Gated on an actual mock session, not merely mock mode being enabled: a mock session exists
  // only when `mock-auth` is set in localStorage, so mock mode alone means a real Cognito login
  // with a mocked backend, and returning here would skip teardown for a session that is
  // genuinely live. `restoreSession` gates on the same pair.
  if (env.VITE_ENABLE_MOCKS) {
    const { hasMockSession, setMockSession } = await import('./mock-session');
    if (hasMockSession()) {
      // Nothing was ever exchanged with Cognito, so there is nothing to revoke, and navigating
      // to the real /logout would send a mock session to Cognito.
      setMockSession(false);
      setBearerToken(null);
      window.location.assign(new URL(env.VITE_COGNITO_REDIRECT_SIGN_OUT).pathname);
      return;
    }
  }

  // Handle uninitialized OIDC (config fetch failed, user on /session-error). Skip revocation
  // (nothing to revoke) and redirect to the sign-out path directly.
  if (!isOidcInitialized()) {
    setBearerToken(null);
    window.location.assign(new URL(env.VITE_COGNITO_REDIRECT_SIGN_OUT).pathname);
    return;
  }

  try {
    await getUserManager().revokeTokens(['refresh_token']);
  } catch (error) {
    // Best-effort: local teardown and the redirect below must still happen. Reported, not
    // swallowed — a missing revocation endpoint would otherwise leave every logout with the
    // refresh token still live at Cognito while looking successful.
    onTeardownError?.(error, 'revoke');
  }

  try {
    await getUserManager().removeUser();
  } catch (error) {
    // Caught for the same reason: the redirect below ends Cognito's pool-wide session cookie,
    // and an uncaught throw here would skip it — the account-takeover case this function
    // exists to prevent.
    onTeardownError?.(error, 'remove');
  }
  setBearerToken(null);

  const url = new URL('/logout', getHostedUiOrigin());
  url.searchParams.set('client_id', getClientId());
  // The `logout_uri` form, not `redirect_uri`+`response_type`+`scope`: validated against the
  // app client's Allowed sign-out URLs (not callback URLs), exact-match per environment.
  url.searchParams.set('logout_uri', env.VITE_COGNITO_REDIRECT_SIGN_OUT);
  // Here and nowhere else: only this branch clears the hosted cookie, which is what lets
  // /auth/signed-out continue straight to sign-in, or to the hosted forgot-password page when
  // `next` asks for it (see `markCognitoSignOut`).
  markCognitoSignOut(next);
  window.location.assign(url.toString());
}
